const TOKEN_STORAGE_KEY = "podwisegpt.jwt";

export type StoredToken = string;

const hasWindow = typeof window !== "undefined";

let capturedMagicToken: string | null = null;

export function getStoredToken(): StoredToken | null {
  if (!hasWindow) return null;
  return window.localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function setStoredToken(token: StoredToken) {
  if (!hasWindow) return;
  window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

export function clearStoredToken() {
  if (!hasWindow) return;
  window.localStorage.removeItem(TOKEN_STORAGE_KEY);
}

export function hasStoredToken(): boolean {
  return getStoredToken() !== null;
}

export function getMagicTokenFromUrl(href: string): string | null {
  try {
    const url = new URL(href);
    const fragment = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
    const fragmentParams = new URLSearchParams(fragment.startsWith("?") ? fragment.slice(1) : fragment);
    const token = fragmentParams.get("token")?.trim()
      ?? url.searchParams.get("token")?.trim();
    return token || null;
  } catch {
    return null;
  }
}

/**
 * Capture and synchronously remove a magic-link capability before React starts.
 * New links use a fragment so the capability is never sent in the HTTP request;
 * query parsing remains temporarily for links issued by an older backend.
 */
export function captureMagicTokenFromCurrentUrl(): string | null {
  if (!hasWindow) return null;

  const url = new URL(window.location.href);
  const token = getMagicTokenFromUrl(url.href);
  if (!token) return null;

  capturedMagicToken = token;
  url.searchParams.delete("token");
  const fragment = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
  const fragmentParams = new URLSearchParams(fragment.startsWith("?") ? fragment.slice(1) : fragment);
  fragmentParams.delete("token");
  const remainingFragment = fragmentParams.toString();
  url.hash = remainingFragment ? `#${remainingFragment}` : "";
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  return capturedMagicToken;
}

export function getCapturedMagicToken(): string | null {
  return capturedMagicToken;
}

export function clearCapturedMagicToken(): void {
  capturedMagicToken = null;
}

export function isCurrentAuthRequest(
  requestToken: string | null,
  currentToken: string | null,
): boolean {
  return requestToken !== null && requestToken === currentToken;
}
